import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import type { NomineeDirectorDetailed, NomineeDirectorTypeSummary } from '@/types'
import { AddProtectedPersonSheet } from './sheets/AddProtectedPersonSheet'
import { EditProtectedPersonSheet } from './sheets/EditProtectedPersonSheet'
import {
  UserPlus,
  Users,
  CreditCard,
  Trash2,
  FileText,
  ChevronRight,
} from 'lucide-react'
import { FieldValues } from '@/components/applicant/FieldValues'
import type { FieldDefinition, FieldValueData } from '@/components/applicant/FieldValues'
import { ProgressStepper } from '@/components/shared/ProgressStepper'

const STAGE_KEYS = ['application', 'payment', 'active', 'ended'] as const
const STAGE_LABELS: Record<string, string> = {
  application: 'Application',
  payment: 'Paid',
  active: 'Active',
  ended: 'Ended',
}


type LocationItem = { id: string; code: string; name: string }
type StateItem = LocationItem & { country_id: string }
type LocalAreaItem = LocationItem & { state_id: string }

type Props = {
  nominee_director: NomineeDirectorDetailed
  type: NomineeDirectorTypeSummary
  countries: LocationItem[]
  states: StateItem[]
  local_areas: LocalAreaItem[]
  fields: FieldDefinition[]
  field_values: FieldValueData[]
}

export default function NomineeDirectorShow({ nominee_director, type, countries, states, local_areas, fields, field_values }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()
  const [addPersonOpen, setAddPersonOpen] = useState(false)
  const [editPerson, setEditPerson] = useState<typeof nominee_director.protected_persons[number] | null>(null)

  if (!nominee_director) {
    return (
      <DashboardLayout title="Service Not Found">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Users className="w-8 h-8 text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Service not found</h3>
          <Button variant="outline" asChild>
            <Link href={`${basePath}/nominee_directors`}>Back to nominee directors</Link>
          </Button>
        </div>
      </DashboardLayout>
    )
  }

  const serviceFee = Math.round(type.service_fee_cents * nominee_director.duration_months / 12)
  const govFee = Math.round(type.gov_fee_cents * nominee_director.duration_months / 12)
  const totalCost = serviceFee + govFee

  return (
    <DashboardLayout title={nominee_director.business_name} breadcrumbs={[{ label: 'Nominee Directors', href: 'nominee_directors' }, { label: nominee_director.business_name }]}>
      <div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{nominee_director.business_name}</h1>
            <StageBadge stage={nominee_director.stage} variant="service" />
          </div>
          <p className="text-sm text-muted-foreground">
            {type.name} · {type.jurisdiction_label} · {nominee_director.duration_months} months
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/nominee_directors/${nominee_director.id}/documents`}>
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Documents
                {nominee_director.documents_count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 font-normal text-xs px-1.5 py-0">{nominee_director.documents_count}</Badge>
                )}
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/nominee_directors/${nominee_director.id}/payment`}>
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                Payment
                {nominee_director.payment?.paid && (
                  <Badge className="ml-1.5 font-normal text-xs px-1.5 py-0 bg-green-600 text-white">Paid</Badge>
                )}
              </Link>
            </Button>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8 py-4 px-5 border rounded-lg">
          <ProgressStepper stages={STAGE_KEYS} labels={STAGE_LABELS} currentStage={nominee_director.stage} />
        </div>

        {/* Get Started callout - shows when payment not yet made */}
        {(() => {
          const missing: { label: string; description: string; href: string; icon: React.ReactNode }[] = []
          if (!nominee_director.payment || !nominee_director.payment.paid) missing.push({ label: 'Complete payment', description: `Pay ${formatCurrency(totalCost)} to activate your nominee director service`, href: `${basePath}/nominee_directors/${nominee_director.id}/payment`, icon: <CreditCard className="w-4 h-4 text-muted-foreground" /> })
          if (missing.length === 0) return null
          return (
            <div className="mb-8">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Get Started</h2>
              <div className="rounded-lg border border-dashed bg-muted/30 divide-y divide-dashed">
              {missing.map(item => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground">{item.label}</div>
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </Link>
              ))}
              </div>
            </div>
          )
        })()}

        {/* Content sections */}
        <div className="space-y-6">

          {/* Protected Persons */}
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Protected Persons</h2>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAddPersonOpen(true)}>
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                Add person
              </Button>
            </div>
            <div className="px-4">
              {nominee_director.protected_persons?.length > 0 ? (
                <div className="divide-y">
                  {nominee_director.protected_persons.map((p) => (
                    <div
                      key={p.id}
                      className="group flex items-center justify-between py-3 cursor-pointer hover:bg-muted/50 -mx-4 px-4 transition-colors"
                      onClick={() => setEditPerson(p)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <span className="text-xs font-medium text-muted-foreground">
                            {p.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{p.full_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.email && <span>{p.email} · </span>}
                            <span className="capitalize">{p.person_kind}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                          onClick={async (e) => {
                            e.stopPropagation()
                            if (await confirm({ description: 'Remove this person?', confirmLabel: 'Remove', destructive: true })) {
                              router.delete(`${basePath}/nominee_directors/${nominee_director.id}/protected_persons/${p.id}`)
                            }
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">No protected persons added yet</p>
                  <Button variant="outline" size="sm" onClick={() => setAddPersonOpen(true)}>
                    Add first person
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* Form Fields */}
          {fields.length > 0 && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-3">Form Data</p>
              <FieldValues fields={fields} values={field_values} />
            </section>
          )}

        </div>
      </div>

      {/* Slideouts */}
      <AddProtectedPersonSheet
        nomineeDirectorId={nominee_director.id}
        countries={countries}
        states={states}
        localAreas={local_areas}
        open={addPersonOpen}
        onOpenChange={setAddPersonOpen}
      />
      <EditProtectedPersonSheet
        nomineeDirectorId={nominee_director.id}
        person={editPerson}
        countries={countries}
        states={states}
        localAreas={local_areas}
        open={!!editPerson}
        onOpenChange={(open) => { if (!open) setEditPerson(null) }}
      />
    </DashboardLayout>
  )
}
